/**
 * O que fazer quando uma requisição leva 401 com a página aberta — compartilhado pelas 5 instâncias axios do projeto
 * (`services/api`, `lib/apiClient`, `faqApi`, `partnerOnboardingApi`, `moduleManagementApi`).
 *
 * Antes, o 1º 401 de QUALQUER chamada apagava o `seweb_auth` e mandava para `/login`, sem tentar renovar, mesmo com um refresh válido
 * guardado: bastava o access vencer com a aba aberta (ou um 401 solto de uma chamada) para a pessoa ser derrubada. Agora o 401 tenta a
 * renovação única (`renovarSessao`) e repete o pedido UMA vez; só derruba quando a renovação é recusada ou a repetição leva 401 de novo.
 *
 * Esta função NÃO apaga nada nem redireciona: devolve o desfecho e cada instância decide (a ação de "sair" — apagar o `seweb_auth`,
 * largar a marca do push e ir ao `/login` — fica no interceptor, onde já morava). Módulo sem contexto e sem `@/services/api`, pelo mesmo
 * motivo de `renovarSessao`: as instâncias o importam, ele não pode importar nenhuma delas.
 */
import { AxiosHeaders, isAxiosError } from "axios";
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { CHAVE_DA_SESSAO, renovarSessao } from "./renovarSessao";

/**
 * - `repetida`: o pedido foi refeito com a sessão renovada; `resposta` é a dele — o interceptor a devolve no lugar do erro.
 * - `sair`: a sessão acabou de verdade — o interceptor apaga o `seweb_auth` e manda para o `/login`.
 * - `esperar`: a renovação não provou nada (manutenção, rede, prazo) — a sessão FICA e o interceptor só rejeita; quem refaz a tentativa,
 *   dentro da janela de 120 s do backend, é o timer do `AuthContext` (30 s depois de um `"tentar_depois"`).
 */
export type DesfechoDo401 =
  | { tipo: "repetida"; resposta: AxiosResponse }
  | { tipo: "sair" }
  | { tipo: "esperar" };

/** A marca vai no próprio `config` do axios (o `mergeConfig` do `request` a preserva), então a repetição já nasce marcada. */
interface ConfigRenovavel extends InternalAxiosRequestConfig {
  _renovado?: boolean;
}

/**
 * Login, refresh e logout NUNCA renovam: o 401 deles é a resposta (senha errada, refresh recusado), não um access vencido — renovar
 * pelo 401 do próprio refresh seria laço. Reconhecidos pelo FIM do caminho, com ou sem `baseURL` na frente e sem a query.
 */
const PEDIDO_DE_AUTENTICACAO = /(?:^|\/)auth\/(?:login|refresh|logout)\/?$/;

function ehPedidoDeAutenticacao(url: string | undefined): boolean {
  const caminho = (url ?? "").split(/[?#]/)[0];
  return PEDIDO_DE_AUTENTICACAO.test(caminho);
}

/** O token que o pedido usou (do `Authorization: Bearer <x>`), ou `null` se o pedido saiu sem ele. */
function tokenDoPedido(config: InternalAxiosRequestConfig): string | null {
  const cabecalho = AxiosHeaders.from(config.headers).get("Authorization");
  if (typeof cabecalho !== "string") return null;
  const token = cabecalho.replace(/^Bearer\s+/i, "");
  return token === "" ? null : token;
}

/** O `access_token` que está guardado AGORA, ou `null` (sem sessão, JSON ilegível ou armazenamento bloqueado). */
function tokenGuardado(): string | null {
  try {
    const bruto = window.localStorage.getItem(CHAVE_DA_SESSAO);
    if (!bruto) return null;
    const sessao: unknown = JSON.parse(bruto);
    const token = (sessao as { access_token?: unknown } | null)?.access_token;
    return typeof token === "string" && token !== "" ? token : null;
  } catch {
    return null;
  }
}

export async function tratar401(
  instancia: AxiosInstance,
  erro: AxiosError
): Promise<DesfechoDo401> {
  const config = erro.config as ConfigRenovavel | undefined;
  // Sem `config` não há pedido para refazer (nunca vem assim de uma resposta do axios): fica o comportamento de sempre.
  if (!config) return { tipo: "sair" };
  // `_renovado`: este pedido já é a repetição de um 401 — um 2º 401 seguido de renovação seria laço.
  if (config._renovado || ehPedidoDeAutenticacao(config.url)) return { tipo: "sair" };

  config._renovado = true;

  // 401 ATRASADO: o pedido saiu com um token e, enquanto a resposta vinha, outra aba (ou o timer do `AuthContext`) já renovou. Renovar
  // de novo giraria o refresh à toa; basta repetir, que o interceptor de pedido relê o token novo. Sem token no pedido, ou sem sessão
  // agora, não há o que comparar e vale o fluxo normal (a renovação diz `"recusou"` se a sessão acabou).
  const doPedido = tokenDoPedido(config);
  const deAgora = tokenGuardado();
  const jaTrocado = doPedido !== null && deAgora !== null && doPedido !== deAgora;

  if (!jaTrocado) {
    const resultado = await renovarSessao();
    if (resultado === "recusou") return { tipo: "sair" };
    if (resultado === "tentar_depois") return { tipo: "esperar" };
  }

  try {
    // O interceptor de pedido da instância relê o `seweb_auth`, então o `Authorization` da repetição já é o do par novo.
    return { tipo: "repetida", resposta: await instancia.request(config) };
  } catch (erroDaRepeticao) {
    // A repetição também levou 401: o par que acabou de ser gravado não vale — a sessão acabou. (O interceptor da própria instância,
    // ao ver a repetição marcada, já devolveu "sair" uma vez; a ação de sair é idempotente.) Qualquer outro erro é o da repetição e
    // segue para quem chamou como ele é — transformar um 500 em "sessão acabou" derrubaria a pessoa sem motivo.
    if (isAxiosError(erroDaRepeticao) && erroDaRepeticao.response?.status === 401) {
      return { tipo: "sair" };
    }
    throw erroDaRepeticao;
  }
}
